import { dollarsToCents } from '@fruitful/fruitils';
import {
    type AccountSummary,
    BillActivationStatus,
    BillCategory,
    BillingCadence,
    Provider,
} from '@fruitful/types';
import { useAtomValue } from 'jotai/index';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Label,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { useUpsertBillAndRefetch } from '../../../bills/useUpsertBill';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { selectedMembershipIdAtom } from '../../entityAtoms';

export const BillCreateModal = ({
    isOpen,
    onClose,
    accounts,
}: {
    isOpen: boolean;
    onClose: () => void;
    accounts: AccountSummary[];
}) => {
    const resetForm = () => {
        setValidationErrors({});

        setName('');
        setAmount(0);
        setCategory(BillCategory.SUBSCRIPTIONS);
        setStatus(BillActivationStatus.ACTIVE);
        setCadence(BillingCadence.MONTHLY);
        setAccountId(accounts[0]?.id ?? undefined);
    };

    const [name, setName] = useState('');
    const [accountId, setAccountId] = useState<string | undefined>(undefined);
    const [amount, setAmount] = useState(0);
    const [category, setCategory] = useState(BillCategory.SUBSCRIPTIONS);
    const [cadence, setCadence] = useState(BillingCadence.MONTHLY);
    const [status, setStatus] = useState(BillActivationStatus.ACTIVE);
    const [showSpinner, setShowSpinner] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        name?: string;
        accountId?: string;
        amount?: string;
    }>({});

    const membershipId = useAtomValue(selectedMembershipIdAtom);
    useEffect(() => {
        resetForm();
    }, [isOpen]);
    const toast = useToastController();
    const { upsertBillAsync } = useUpsertBillAndRefetch(membershipId ?? '');
    const handleSave = async () => {
        const errors: typeof validationErrors = {};
        const account = accounts.find((account) => account.id === accountId);

        if (!name.trim()) errors.name = 'Name is required.';
        if (!account) errors.accountId = 'Must have an account selected.';
        if (amount <= 0) errors.amount = 'Amount must be greater than 0.';

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setShowSpinner(true);

        try {
            const upsertBillBody = {
                membershipId: membershipId ?? '',
                displayName: name,
                amount: dollarsToCents(amount),
                accountId: account?.id ?? '',
                category,
                activationStatus: status,
                cadence,
                payee: name,
            };

            await upsertBillAsync(upsertBillBody);

            onClose();

            toast.show('Bill created', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error creating bill', MutationErrorToastOptions());
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            hasCloseButton
            // @ts-expect-error style tag is valid here
            style={{ width: 500 }}
        >
            <YStack
                gap={16}
                padding={16}
                width={600}
                overflow="scroll"
                maxHeight={600}
            >
                {accounts.length < 1 ? (
                    <>
                        <BodyLargeRegular paddingTop={24} textAlign="center">
                            Make sure there are linked accounts or manual
                            accounts added before adding bills
                        </BodyLargeRegular>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SMALL}
                            text="Ok"
                            onPress={onClose}
                        />
                    </>
                ) : (
                    <>
                        <BodyLargeEmphasized>
                            Create Manual Bill
                        </BodyLargeEmphasized>

                        <YStack>
                            <Label htmlFor="name">Name</Label>
                            <InputText
                                id="name"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value ?? '');
                                    validationErrors.name = undefined;
                                }}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                }}
                                disabled={showSpinner}
                            />
                            {validationErrors.name !== null && (
                                <BodyMediumRegular color="red">
                                    {validationErrors.name}
                                </BodyMediumRegular>
                            )}
                        </YStack>

                        <YStack>
                            <Label htmlFor="account">Account</Label>
                            <select
                                id="account"
                                value={accountId}
                                onChange={(e) => {
                                    setAccountId(e.target.value);
                                }}
                                disabled={showSpinner}
                                style={{
                                    padding: 12,
                                    borderRadius: 8,
                                    borderWidth: 1,
                                    borderColor: '#ccc',
                                    fontSize: 16,
                                }}
                            >
                                {Object.values(accounts).map((value) => (
                                    <option key={value.id} value={value.id}>
                                        {value.name} -{' '}
                                        {value.provider === Provider.MANUAL
                                            ? 'Manual'
                                            : (value.accountNumberMasked ??
                                              '????')}
                                    </option>
                                ))}
                            </select>
                            {validationErrors.accountId !== null && (
                                <BodyMediumRegular color="red">
                                    {validationErrors.accountId}
                                </BodyMediumRegular>
                            )}
                        </YStack>

                        <YStack>
                            <Label htmlFor="category">Category</Label>
                            <select
                                id="category"
                                value={category}
                                onChange={(e) => {
                                    setCategory(e.target.value as BillCategory);
                                }}
                                disabled={showSpinner}
                                style={{
                                    padding: 12,
                                    borderRadius: 8,
                                    borderWidth: 1,
                                    borderColor: '#ccc',
                                    fontSize: 16,
                                }}
                            >
                                {Object.values(BillCategory).map((value) => (
                                    <option key={value} value={value}>
                                        {value
                                            .replace(/_/g, ' ')
                                            .replace(/\b\w/g, (l) =>
                                                l.toUpperCase(),
                                            )}
                                    </option>
                                ))}
                            </select>
                        </YStack>

                        <YStack>
                            <Label htmlFor="status">Status</Label>
                            <select
                                id="status"
                                value={status}
                                onChange={(e) => {
                                    setStatus(
                                        e.target.value as BillActivationStatus,
                                    );
                                }}
                                disabled={true}
                                style={{
                                    padding: 12,
                                    borderRadius: 8,
                                    borderWidth: 1,
                                    borderColor: '#ccc',
                                    fontSize: 16,
                                }}
                            >
                                <option value="ACTIVE">Active</option>
                                <option value="REVIEW">Review</option>
                                <option value="REJECTED">Rejected</option>
                            </select>
                        </YStack>

                        <YStack>
                            <Label htmlFor="cadence">Cadence</Label>
                            <select
                                id="cadence"
                                value={cadence}
                                onChange={(e) => {
                                    setCadence(
                                        e.target.value as BillingCadence,
                                    );
                                }}
                                disabled={showSpinner}
                                style={{
                                    padding: 12,
                                    borderRadius: 8,
                                    borderWidth: 1,
                                    borderColor: '#ccc',
                                    fontSize: 16,
                                }}
                            >
                                {Object.values(BillingCadence)
                                    .filter(
                                        (value) =>
                                            value !== BillingCadence.YEARLY,
                                    )
                                    .map((value) => (
                                        <option key={value} value={value}>
                                            {value
                                                .replace(/_/g, '-')
                                                .toLowerCase()
                                                .replace(/\b\w/g, (c) =>
                                                    c.toUpperCase(),
                                                )}
                                        </option>
                                    ))}
                            </select>
                        </YStack>

                        <YStack>
                            <Label htmlFor="amount">Amount</Label>
                            <InputNumber
                                id="amount"
                                disabled={showSpinner}
                                value={amount}
                                onValueChange={(e) => {
                                    validationErrors.amount = undefined;
                                    setAmount(e.value ?? 0);
                                }}
                                mode="currency"
                                currency="USD"
                                min={0}
                            />
                            {validationErrors.amount !== null && (
                                <BodyMediumRegular color="red">
                                    {validationErrors.amount}
                                </BodyMediumRegular>
                            )}
                        </YStack>

                        {showSpinner ? (
                            <XStack gap={12}>
                                <Spinner />
                            </XStack>
                        ) : (
                            <XStack gap={12}>
                                <Button
                                    flex={1}
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    tone="neutral"
                                    text="Cancel"
                                    onPress={onClose}
                                />
                                <Button
                                    flex={1}
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    tone="positive"
                                    text="Save"
                                    onPress={handleSave}
                                />
                            </XStack>
                        )}
                    </>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
